import React, { useCallback, useState } from 'react';
import { Formik, Form } from 'formik';
import { UserInputs, specialsSchema, specialsInitialValues } from '.';
import {
    callVerifyHuman,
    NETLIFY_FUNCTIONS_PATH,
    getFacebookCookies,
    getOptimizelyData,
    appendFormUrlToExposures,
    syncPardotTracking,
    isLeadsCaptureResponse,
    validateWithPossibleNow,
    appendGoogleLinkerParam,
    safeFullStory as FullStory,
} from '../../../utils';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';
import './Discount.css';
import { FormFailed, SelfScheduleSubmitting, ConsentMsg, LocationsDropdownWrapper } from '../shared';
import { trackSubmitLeadGenerationForm } from '../../../optimizely/optimizely';
import { useSelfScheduleTimer, SELF_SCHEDULE_EVENTS } from '../../../hooks/useSelfScheduleTimer';

export function DiscountForm({ onClose, siteData }) {
    const apiEndpoint = `${NETLIFY_FUNCTIONS_PATH}consult-form-handler`;

    const isBrowser = typeof window !== 'undefined';

    // WEBDEV-3553: See ConsultationForm for full explanation. Same pattern applies here —
    // isRedirecting keeps SelfScheduleSubmitting visible after Formik resets isSubmitting.
    const [isRedirecting, setIsRedirecting] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();
    const { startTimer, pushTimingEvent, resetTimer } = useSelfScheduleTimer();

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }
        const user = await callVerifyHuman('corporateConsult', executeRecaptcha);
        return user;
    }, [executeRecaptcha]);

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        helpers.setSubmitting(true);

        // Start timer for self-schedule flow (DiscountForm is always self-schedule)
        startTimer({
            form_type: 'discount',
        });

        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();

        try {
            const city = siteData.selectedStore?.metro?.name;
            const state = siteData.selectedStore?.state?.abbreviation;
            const cloudamp__data__c = localStorage.getItem('cloudamp__data__c') || '';
            const user = await handleReCaptchaVerify();
            if (user === 'bot') {
                helpers.setErrors('bot', true);
                throw new Error('Bot detected');
            }

            // Validate phone and email with PossibleNow
            const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                values.phone,
                values.email
            );

            const formData = {
                ...values,
                consultType: 'Self Schedule',
                formSource: 'specials',
                updates: true, // TCPA compliance
                city,
                state,
                cloudamp__data__c,
                fbp,
                fbc,
                experiment_exposures: appendFormUrlToExposures(experiment_exposures),
                experiment_user_ID,
                phoneFound,
                wireless,
                emailFound,
                result,
                flags,
            };

            trackSubmitLeadGenerationForm();

            // Submit to Netlify function
            const response = await axios.post(apiEndpoint, formData);

            // Only sync Pardot tracking on the legacy path; leads-capture already wrote the lead.
            if (response?.status === 200 && !isLeadsCaptureResponse(response)) {
                syncPardotTracking(values);
            }

            // FullStory Event Tracking
            FullStory('trackEvent', {
                name: 'Discount Form Modal - Success',
                properties: { name: 'Discount Form Modal', user_email: values.email },
            });

            // If backend returned a Self-Schedule URL, perform a top-level redirect
            if (isBrowser && response.data.redirectUrl) {
                // Lock the overlay before the GTM await — see isRedirecting declaration above.
                // Must be set BEFORE setSubmitting(false); otherwise React flushes the
                // isSubmitting=false update first, causing a flash before isRedirecting=true applies.
                setIsRedirecting(true);
                // Push timing event to GTM before redirect
                // Wait for event to be sent before redirecting
                await pushTimingEvent(SELF_SCHEDULE_EVENTS.TIMING, {
                    form_type: 'discount',
                });

                helpers.resetForm();
                let redirectUrl = response.data.redirectUrl;
                // Append GA4 cross-domain linker parameter for tracking
                redirectUrl = appendGoogleLinkerParam(redirectUrl);
                window.location.href = redirectUrl;
                onClose();
                return;
            } else {
                helpers.resetForm();
            }
        } catch (error) {
            console.error('Form submission error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Form submission error';

            // Track failure and reset timer on error
            await pushTimingEvent(SELF_SCHEDULE_EVENTS.FAILURE, {
                form_type: 'discount',
                error_type: errorMessage,
            });
            await resetTimer();

            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setIsRedirecting(false);

            // FullStory Event Tracking with enhanced error details
            FullStory('trackEvent', {
                name: 'Discount Form Modal - Error',
                properties: {
                    name: 'Discount',
                    user_email: values.email,
                    error_message: errorMessage,
                    error_details: axios.isAxiosError(error)
                        ? {
                              status: error.response?.status,
                              statusText: error.response?.statusText,
                          }
                        : 'Non-axios error',
                },
            });

            // Log that Pardot tracking was skipped due to form submission failure
            FullStory('trackEvent', {
                name: 'Discount Form Modal - Pardot Sync Skipped',
                properties: {
                    user_email: values.email,
                    error_message: errorMessage,
                    reason: 'Actual Form submission failed',
                },
            });

            setTimeout(() => helpers.resetForm(), 5000);
        }
    };

    return (
        <div id="discount-modal-form">
            <Formik
                initialValues={specialsInitialValues}
                validationSchema={specialsSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {(formik) => (
                    <Form className="container fs-mask" method="POST">
                        {(formik.isSubmitting || isRedirecting) && <SelfScheduleSubmitting />}
                        {!isRedirecting && formik.status === 'Failed' && <FormFailed onClose={onClose} />}

                        {!formik.isSubmitting && !isRedirecting && formik.status !== 'Failed' && (
                            <>
                                <UserInputs formik={formik} />
                                <div className="row locations-container mb-2">
                                    <LocationsDropdownWrapper
                                        {...{ siteData, formik, isModal: true }}
                                        fieldName="location"
                                    />
                                </div>

                                <div className="row justify-content-center mb-2">
                                    <div className="lh-1">
                                        <ConsentMsg cta={'Unlock Offer'} />
                                    </div>
                                </div>

                                <div
                                    id="actions-btns"
                                    className="row flex-sm-row-reverse gx-1 gy-2 justify-content-center"
                                >
                                    <div className="text-center">
                                        <button
                                            id="corporate-schedule-btn-modal"
                                            type="submit"
                                            className="milan-btn w-100"
                                            disabled={!formik.isValid || formik.status === 'BOT'}
                                        >
                                            {formik.isSubmitting ? 'Submitting...' : 'Unlock offer'}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </Form>
                )}
            </Formik>
        </div>
    );
}

export default DiscountForm;
